import SearchComponent from '@components/inputs/search/Search.component';
import SelectComponent from '@components/inputs/select/Select.component';
import ConfirmModal from '@components/modal/Confirm.modal';
import NewTableComponent from '@components/table/NewTable.component';
import { Box, Stack } from '@mui/material';
import { GridValueGetterParams } from '@mui/x-data-grid';
import { useEffectFn } from '@ngneat/effects-hooks';
import { emitOnce } from '@ngneat/elf';
import { useObservable } from '@ngneat/react-rxjs';
import { Audit } from '@store/audits/audits.model';
import { searchTemplatesEffect } from '@store/audits/templates/templates.effects';
import { Templates } from '@store/audits/templates/templates.model';
import { templatesQuery } from '@store/audits/templates/templates.query';
import { templatesService } from '@store/audits/templates/templates.service';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import { ConfirmActionProps, OtherActionType } from '@utils/table/columnTypes/actions.columnType';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Observable, finalize } from 'rxjs';
import { Audits } from '../Audits.screen';


const TemplatesComponent = () => {
  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);
  const [loading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const searchTemplates = useEffectFn(searchTemplatesEffect);
  const [selectedTemplates] = useObservable(templatesQuery.selectedTemplates$);


  const [{ templates }] = useObservable(templatesQuery.templates$);
  const [filters] = useObservable(templatesQuery.filters$);
  const [sortModel] = useObservable(templatesQuery.sort$);
  const [tablePagination] = useObservable(templatesQuery.pagination$);

  useEffect(() => {
    searchTemplates({ filters, tablePagination, sort: sortModel });
  }, [filters, searchTemplates, sortModel, tablePagination]);

  const handleDelete = (params: any) => {
    setOpenConfirmAction({
      type: OtherActionType.DELETE,
      row: params
    });
  };


  const handleConfirmValidation = (type: OtherActionType, audit: Audit) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;

    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = templatesService.deleteTemplate(audit.id);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar('Votre template a été supprimé avec succès!', { variant: 'success' });
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };


  return (
    <>
      <Stack direction="row" spacing={2}>
        <SelectComponent
          color="primary"
          size="small"
          inputWidth={261}
          placeholder="Toutes les entreprises"
          items={[]}
          handleChange={() => undefined}
        />
        <Box width="100%">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                templatesService.setFilters('search')(value);
                templatesService.setPagination('page')(1);
              });
            }}
          />
        </Box>
      </Stack>
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.TEXT_BOLD({
            field: 'name',
            width: 400,
            label: 'Template',
            sortable: true
          }),
          {
            field: 'author',
            width: 400,
            headerName: 'Créateur',
            valueGetter: (params: GridValueGetterParams<any, Templates>) => params.row.author.firstName,
            valueFormatter: (params) =>
              params.value ? params.value : '',
          },
          COLUMN_TYPES.TEXT({
            field: 'updatedAt',
            width: 200,
            color: 'secondary',
            label: 'Dernière modification',
            valueGetter: (params: GridValueGetterParams<any, Audits>) => params.row.updatedAt,
            valueFormatter: (params) =>
              params.value ? t('global.dateAndHour', { date: new Date(params.value) }) : '',
          }),
          COLUMN_TYPES.ACTIONS({
            width: 100,
            handleShowRow: (template: Templates) => navigate(`${template.id}/template`),
            actions: [
              {
                type: OtherActionType.DELETE,
                handleClick: (params) => handleDelete(params),
              },
            ],
          }),
        ]}
        handlePageSizeChange={templatesService.setPagination('limit')}
        handlePageChange={(page) => templatesService.setPagination('page')(page + 1)}
        handleSortModelChange={templatesService.setSort}
        disableRowSelectionOnClick={true}
        handleRowSelected={templatesService.setSelectedTemplates}
        rowsSelected={selectedTemplates}
        totalRows={templatesQuery.totalRows}
        data={templates}
      />
      {!!openConfirmAction && (
        <ConfirmModal
          handleClose={() => setOpenConfirmAction(false)}
          description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.template`)}
          handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
          title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
          validateButton={OtherActionType.label(openConfirmAction.type)}
        />
      )}
    </>
  );
};

export default TemplatesComponent;